import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  createTheme,
  ThemeProvider,
  Container,
  Grid,
  Box,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  tableCellClasses,
  TableBody,
  Paper,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Modal,
} from "@mui/material";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SendIcon from "@mui/icons-material/Send";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Footer from "../../../components/footer/Footer";
import Header from "../../../components/header/Header";
import { Formik } from "formik";
import * as yup from "yup";
import {
  createTraveller,
  deleteTraveller,
  getTravellers,
  updateTraveller,
} from "../../../api/traveller";
import TopMenu from "../../../components/topbar/TopMenu";
import {
  decodeTokenRefresh,
  getTokenRefresh,
  verifyExpireTokenRefresh,
} from "../../../helpers/token/refreshToken";
import { getSunatDni } from "../../../api/sunat/sunat";
import SnackBar from "../../../components/snackbar/SnackBar";

dayjs.extend(customParseFormat);

const calculateAgeFromBirthDate = (birthDate) => {
  const isoBirthDate = parseBirthDateToApi(birthDate);
  if (!isoBirthDate) return "";

  const birthday = new Date(`${isoBirthDate}T00:00:00`);
  if (Number.isNaN(birthday.getTime())) return "";

  const today = new Date();
  let age = today.getFullYear() - birthday.getFullYear();
  const monthDiff = today.getMonth() - birthday.getMonth();
  const hasNotHadBirthdayThisYear =
    monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthday.getDate());

  if (hasNotHadBirthdayThisYear) age -= 1;

  return age >= 0 ? age : "";
};

const isValidApiBirthDate = (birthDate) => {
  const [year, month, day] = birthDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const formatApiBirthDateToDisplay = (birthDate) => {
  if (!birthDate) return "";

  const apiBirthDate = String(birthDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(apiBirthDate)) return "";

  const [year, month, day] = apiBirthDate.split("-");
  return `${day}/${month}/${year}`;
};

const parseBirthDateToApi = (birthDate) => {
  if (!birthDate) return null;

  if (dayjs.isDayjs(birthDate)) {
    return birthDate.isValid() ? birthDate.format("YYYY-MM-DD") : null;
  }

  const birthDateValue = String(birthDate);
  if (/^\d{4}-\d{2}-\d{2}$/.test(birthDateValue)) {
    return isValidApiBirthDate(birthDateValue) ? birthDateValue : null;
  }

  const match = birthDateValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  const apiBirthDate = `${year}-${month}-${day}`;

  return isValidApiBirthDate(apiBirthDate) ? apiBirthDate : null;
};

const getDatePickerValue = (birthDate) => {
  const apiBirthDate = parseBirthDateToApi(birthDate);

  return apiBirthDate ? dayjs(apiBirthDate) : null;
};

const formatTravellerBirthDate = (birthDate) => {
  if (!birthDate) return "";

  const apiBirthDate = parseBirthDateToApi(birthDate);

  return apiBirthDate ? formatApiBirthDateToDisplay(apiBirthDate) : "";
};

const normalizeTraveller = (traveller) => ({
  ...traveller,
  birth_date: formatTravellerBirthDate(traveller.birth_date),
});

const normalizeOptionalNumber = (value) => (value === "" ? null : value);

const canConsultDni = (document) => /^\d{8}$/.test(String(document || ""));

const Traveller = () => {
  const theme = createTheme();
  const { t } = useTranslation();
  const [traveller, setTraveller] = useState([]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");

  const [openEditModal, setOpenEditModal] = useState(false);
  const handleOpenEditModal = () => setOpenEditModal(true);
  const handleCloseEditModal = () => setOpenEditModal(false);

  const checkoutSchema = yup.object().shape({
    name: yup.string().required("required"),
    birthDate: yup
      .mixed()
      .test("birth-date-format", "Formato DD/MM/YYYY", (value) => {
        if (!value) return true;

        return Boolean(parseBirthDateToApi(value));
      }),
    age: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    country: yup.string().required("required"),
    dni: yup.string().required("required"),
  });

  const initialValues = {
    name: "",
    birthDate: null,
    age: "",
    country: "",
    dni: "",
  };

  useEffect(() => {
    const travellers = async () => {
      const token = getTokenRefresh();
      const uid = decodeTokenRefresh(token);
      try {
        const response = await getTravellers(token, uid);
        const data = (response.data.travelers || [])
          .filter((traveller) => traveller.id)
          .map(normalizeTraveller);
        setTraveller(data);
      } catch (error) {
        console.log(error);
      }
    };

    travellers();
  }, []);

  const handleFormSubmit = async (values) => {
    try {
      setIsLoading(true);
      const verify = verifyExpireTokenRefresh();
      if (verify) navigate("/");

      const token = getTokenRefresh();
      const birthDate = parseBirthDateToApi(values.birthDate);
      const response = await createTraveller(
        token,
        values.name.toUpperCase(),
        birthDate,
        normalizeOptionalNumber(values.age),
        values.dni.toUpperCase(),
        values.country.toUpperCase(),
      );
      const trave = {
        id: response?.data?.id,
        name: values.name.toUpperCase(),
        birth_date: formatTravellerBirthDate(values.birthDate),
        age: normalizeOptionalNumber(values.age),
        document: values.dni.toUpperCase(),
        country: values.country.toUpperCase(),
      };
      setTraveller((currentTraveller) => [...currentTraveller, trave]);
      showSnackbar("USUARIO AGREGADO");
      setIsLoading(false);
    } catch (err) {
      showSnackbar("ERROR NO SE AGREGO AL USUARIO");
      setIsLoading(false);
      console.log(err);
    }
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 12,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const showSnackbar = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };
  const handleCloseSnk = () => setStateSnk(false);

  const handleMouseDownPassword = (event) => event.preventDefault();

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 2,
  };

  const [userId, setUserId] = useState("");
  const [userName, setUserName] = useState("");
  const [userCountry, setUserCountry] = useState("");
  const [userAge, setUserAge] = useState("");
  const [userBirthDate, setUserBirthDate] = useState(null);
  const [userDocument, setUserDocument] = useState("");

  const editTraveller = async () => {
    try {
      setIsLoading(true);
      const token = getTokenRefresh();
      const birthDate = parseBirthDateToApi(userBirthDate);
      if (userBirthDate && !birthDate) {
        showSnackbar("Formato de fecha DD/MM/YYYY", "error");
        setIsLoading(false);
        return;
      }
      const putTraveller = {
        id: userId,
        name: userName.toUpperCase(),
        birth_date: birthDate,
        document: userDocument.toUpperCase(),
        country: userCountry.toUpperCase(),
        age: normalizeOptionalNumber(userAge),
      };
      await updateTraveller(token, putTraveller);
      setTraveller((currentTraveller) =>
        currentTraveller.map((travel) =>
          travel.id === putTraveller.id
            ? normalizeTraveller(putTraveller)
            : travel,
        ),
      );
      showSnackbar("DATOS DE USUARIO ACTUALIZADO");
      handleCloseEditModal();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      showSnackbar("ERROR NO SE LOGRO ACTUALIZAR DATOS", "error");
      console.log(error);
    }
  };

  const eliminateTraveller = async (id) => {
    try {
      setIsLoading(true);
      const token = getTokenRefresh();
      await deleteTraveller(token, id);
      setTraveller((currentTraveller) =>
        currentTraveller.filter((travel) => travel.id !== id),
      );
      showSnackbar("DATOS DE USUARIO ACTUALIZADO");
      handleCloseEditModal();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      showSnackbar("ERROR NO SE LOGRO ACTUALIZAR DATOS", "error");
      console.log(error);
    }
  };

  return (
    <div>
      <TopMenu />
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="sm" sx={{ p: 1 }}>
          <Header title={t("")} subtitle={t("travel_list")} />
          <Formik
            onSubmit={handleFormSubmit}
            initialValues={initialValues}
            validationSchema={checkoutSchema}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Box
                  gap="30px"
                  onSubmit={handleSubmit}
                  sx={{ mt: 3, margin: "5px" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel htmlFor="outlined-adornment-password">
                          DNI
                        </InputLabel>
                        <OutlinedInput
                          value={values.dni}
                          name="dni"
                          maxRows={1}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          id="control-dni"
                          inputProps={{
                            style: { textTransform: "uppercase" },
                            maxLength: 16,
                          }}
                          endAdornment={
                            canConsultDni(values.dni) ? (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle visibility"
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  onClick={async (e) => {
                                    if (canConsultDni(values.dni)) {
                                      try {
                                        const res = await getSunatDni(
                                          values.dni,
                                        );
                                        console.log(res);
                                        values.name = `${res?.data?.nombres} ${res?.data?.apellidoPaterno} ${res.data?.apellidoMaterno}`;
                                        showSnackbar("Persona Encontrada");
                                      } catch (error) {
                                        showSnackbar(
                                          "DNI no encontrado, Complete Manualmente",
                                          "error",
                                        );
                                      }
                                    } else {
                                      showSnackbar("DNI de 8 digitos", "error");
                                    }
                                  }}
                                >
                                  <SendIcon />
                                </IconButton>
                              </InputAdornment>
                            ) : null
                          }
                          label="DNI"
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        value={values.country}
                        fullWidth
                        type="text"
                        label={t("country")}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name="country"
                        inputProps={{ style: { textTransform: "uppercase" } }}
                        error={!!touched.country && !!errors.country}
                        helperText={touched.country && errors.country}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        value={values.name}
                        fullWidth
                        type="text"
                        label={t("name")}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name="name"
                        error={!!touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                        inputProps={{ style: { textTransform: "uppercase" } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileDatePicker
                          label="Cumpleaños"
                          inputFormat="DD/MM/YYYY"
                          value={values.birthDate}
                          onChange={(birthDate) => {
                            setFieldValue("birthDate", birthDate);
                            setFieldValue(
                              "age",
                              calculateAgeFromBirthDate(birthDate),
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              name="birthDate"
                              onBlur={handleBlur}
                              error={!!touched.birthDate && !!errors.birthDate}
                              helperText={touched.birthDate && errors.birthDate}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        value={values.age}
                        fullWidth
                        type="number"
                        label={t("age")}
                        onBlur={handleBlur}
                        name="age"
                        InputProps={{ readOnly: true }}
                        error={!!touched.age && !!errors.age}
                        helperText={touched.age && errors.age}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                      <Button
                        fullWidth
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isLoading}
                      >
                        {t("add")}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                <SnackBar
                  stateSnk={stateSnk}
                  vertical={vertical}
                  horizontal={horizontal}
                  duration={4000}
                  handleClose={handleCloseSnk}
                  severity={severity}
                  messageSnk={messageSnk}
                />
              </form>
            )}
          </Formik>
        </Container>

        <Container component="main" maxWidth="md" sx={{ p: 1 }}>
          {traveller.length > 0 && (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 600 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>{t("ID")}</StyledTableCell>
                    <StyledTableCell>{t("name").toUpperCase()}</StyledTableCell>
                    <StyledTableCell align="center">
                      {t("age").toUpperCase()}
                    </StyledTableCell>
                    <StyledTableCell align="center">CUMPLEAÑOS</StyledTableCell>
                    <StyledTableCell align="center">
                      {t("dni").toUpperCase()}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {t("country").toUpperCase()}
                    </StyledTableCell>
                    <StyledTableCell align="center"></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {traveller.map((row, index) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell component="th" scope="row">
                        {index + 1}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.name.toUpperCase()}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.age}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.birth_date || ""}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.document}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {row.country.toUpperCase()}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Button
                          color="primary"
                          onClick={(e) => {
                            e.preventDefault();
                            setUserId(row.id);
                            setUserName(row.name);
                            setUserDocument(row.document);
                            setUserAge(row.age);
                            setUserBirthDate(
                              getDatePickerValue(row.birth_date),
                            );
                            setUserCountry(row.country);
                            handleOpenEditModal();
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          color="error"
                          onClick={(e) => {
                            e.preventDefault();
                            eliminateTraveller(row.id);
                          }}
                        >
                          Delete
                        </Button>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Container>

        <Modal open={openEditModal} onClose={handleCloseEditModal}>
          <Box sx={style}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12}>
                <TextField
                  value={userName}
                  fullWidth
                  type="text"
                  label={t("name")}
                  onChange={(e) => setUserName(e.target.value)}
                  name="name"
                  inputProps={{
                    style: { textTransform: "uppercase" },
                  }}
                />
              </Grid>
              <Grid item xs={8} sm={8}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="Cumpleaños"
                    inputFormat="DD/MM/YYYY"
                    value={userBirthDate}
                    onChange={(birthDate) => {
                      setUserBirthDate(birthDate);
                      setUserAge(calculateAgeFromBirthDate(birthDate));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth name="birthDate" />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={4} sm={4}>
                <TextField
                  value={userAge}
                  fullWidth
                  type="number"
                  label={t("age")}
                  name="age"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  value={userCountry}
                  fullWidth
                  type="text"
                  label={t("country")}
                  name="country"
                  onChange={(e) => setUserCountry(e.target.value)}
                  inputProps={{
                    style: { textTransform: "uppercase" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="outlined-adornment-password">
                    DNI
                  </InputLabel>
                  <OutlinedInput
                    value={userDocument}
                    name="dni"
                    maxRows={1}
                    onChange={(e) => setUserDocument(e.target.value)}
                    id="control-dni"
                    inputProps={{
                      style: { textTransform: "uppercase" },
                      maxLength: 16,
                    }}
                    endAdornment={
                      canConsultDni(userDocument) ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle visibility"
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            onClick={async (e) => {
                              if (canConsultDni(userDocument)) {
                                try {
                                  const res = await getSunatDni(userDocument);
                                  setUserName(
                                    `${res?.data?.nombres} ${res?.data?.apellidoPaterno} ${res.data?.apellidoMaterno}`,
                                  );
                                  showSnackbar("Persona Encontrada");
                                } catch (error) {
                                  showSnackbar("DNI no encontrado", "error");
                                }
                              } else {
                                showSnackbar("DNI de 8 digitos", "error");
                              }
                            }}
                          >
                            <SendIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                    label="DNI"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  disabled={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    editTraveller();
                  }}
                >
                  {t("ACTUALIZAR")}
                </Button>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Button
                  fullWidth
                  color="error"
                  variant="contained"
                  onClick={(e) => {
                    e.preventDefault();
                    handleCloseEditModal();
                  }}
                >
                  {t("cancel")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Modal>
      </ThemeProvider>
      <Footer />
    </div>
  );
};

export default Traveller;
